import {
  WalletBalance,
  Contract,
  AllowedInstrument,
} from "@starx/shared-types";
import { RestClient } from "../api/rest_client";
import { getLiveUsdInrRate, getLiveUsdFxRate } from "../../../shared-web/display-currency";

/**
 * Format balance cleanly:
 * - Max 2 decimal places only when balance has decimals (e.g. 10410.25, 10410.50)
 * - Whole numbers are properly rounded off without trailing .00 (e.g. 10410 instead of 10410.0000000)
 */
export function formatSmartBalance(val: unknown): string {
  const raw = String(val ?? "").trim();
  if (!raw || !/^-?\d+(?:\.\d+)?$/.test(raw)) return "0";
  const num = Number(raw);
  if (isNaN(num)) return "0";
  const sign = num < 0 ? "-" : "";
  const abs = Math.abs(num);
  const rounded = Math.round(abs * 100) / 100;
  if (Math.round(rounded * 100) % 100 === 0) {
    return `${sign}${Math.round(rounded)}`;
  }
  return `${sign}${rounded.toFixed(2)}`;
}

export function getCurrencySymbol(curr?: string): string {
  const c = curr?.toUpperCase() || "USD";
  switch (c) {
    case "INR": return "₹";
    case "EUR": return "€";
    case "GBP": return "£";
    case "JPY": return "¥";
    case "CAD": return "CA$";
    case "AUD": return "A$";
    case "NZD": return "NZ$";
    case "CHF": return "CHF ";
    case "BRL": return "R$";
    case "AED": return "AED ";
    case "SAR": return "﷼ ";
    case "QAR": return "QR ";
    case "KWD": return "KD ";
    case "ZAR": return "R ";
    case "NGN": return "₦";
    case "GHS": return "GH₵ ";
    case "KES": return "KSh ";
    case "IDR": return "Rp ";
    case "PHP": return "₱";
    case "BDT": return "৳";
    case "PKR": return "₨ ";
    case "MXN": return "Mex$";
    case "TRY": return "₺";
    case "KRW": return "₩";
    case "HKD": return "HK$";
    case "TWD": return "NT$";
    case "SGD": return "S$";
    case "PLN": return "zł ";
    case "SEK": case "NOK": case "DKK": return "kr ";
    case "USD": case "USDT": case "USDC": return "$";
    default: return `${c} `;
  }
}

export type TradingMode = "DEMO" | "LIVE";

export class AppState {
  private restClient: RestClient;
  private mode: TradingMode = "DEMO";
  private demoBalance: string = "10000.00";
  private liveBalance: WalletBalance | null = null;
  private activeContracts: Contract[] = [];
  private contractHistory: Contract[] = [];
  private demoActiveContracts: Contract[] = [];
  private demoContractHistory: Contract[] = [];
  private instruments: AllowedInstrument[] = [];
  private selectedSymbol: string = "EURUSD_OTC";
  private selectedCurrency: string = "USD";
  private balanceGeneration = 0;
  private listeners = new Set<() => void>();

  constructor(restClient: RestClient) {
    this.restClient = restClient;
    this.loadPersistedState();
  }

  private loadPersistedState() {
    if (typeof localStorage === "undefined") return;
    try {
      const hasToken = Boolean(localStorage.getItem("starx_auth_token"));
      const savedMode = localStorage.getItem("starx_trading_mode");
      if (hasToken && (savedMode === "LIVE" || savedMode === "DEMO")) {
        this.mode = savedMode;
      } else {
        this.mode = "DEMO";
      }
      const savedSymbol = localStorage.getItem("starx_trading_selected_symbol");
      if (savedSymbol && /^[A-Z0-9_]{2,32}$/.test(savedSymbol)) {
        this.selectedSymbol = savedSymbol;
      }
      const savedCurrency = localStorage.getItem("starx_trading_currency");
      const userExplicit = localStorage.getItem("starx_user_explicit_currency");
      if (savedCurrency && /^[A-Z]{3,5}$/.test(savedCurrency)) {
        if (savedCurrency === "CHF" && userExplicit !== "true") {
          this.selectedCurrency = "USD";
          try { localStorage.setItem("starx_trading_currency", "USD"); } catch {}
        } else {
          this.selectedCurrency = savedCurrency;
        }
      }
      const savedBalance = localStorage.getItem("starx_demo_balance");
      if (savedBalance && /^\d+(\.\d+)?$/.test(savedBalance)) {
        this.demoBalance = Number(savedBalance).toFixed(2);
      }
      const savedActive = localStorage.getItem("starx_demo_active_contracts");
      if (savedActive) {
        const parsed = JSON.parse(savedActive);
        if (Array.isArray(parsed)) this.demoActiveContracts = parsed;
      }
      const savedHistory = localStorage.getItem("starx_demo_contract_history");
      if (savedHistory) {
        const parsed = JSON.parse(savedHistory);
        if (Array.isArray(parsed)) this.demoContractHistory = parsed;
      }
    } catch (e) {
      console.warn("Error loading persisted trading state:", e);
    }
  }

  private persistDemoState() {
    if (typeof localStorage === "undefined") return;
    try {
      localStorage.setItem("starx_demo_balance", this.demoBalance);
      localStorage.setItem("starx_demo_active_contracts", JSON.stringify(this.demoActiveContracts));
      localStorage.setItem("starx_demo_contract_history", JSON.stringify(this.demoContractHistory.slice(0, 200)));
      if (this.restClient && localStorage.getItem("starx_auth_token")) {
        void this.restClient.syncDemoBalance(Number(this.demoBalance)).catch(() => {});
      }
    } catch (e) {
      console.warn("Error saving persisted trading state:", e);
    }
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    this.listeners.forEach((fn) => fn());
  }

  public getMode(): TradingMode {
    return this.mode;
  }

  public setMode(mode: TradingMode) {
    if (mode === "LIVE") {
      const hasToken = typeof localStorage !== "undefined" && Boolean(localStorage.getItem("starx_auth_token"));
      if (!hasToken) {
        mode = "DEMO";
      }
    }
    if (this.mode !== mode) {
      this.mode = mode;
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("starx_trading_mode", mode);
      }
      this.notify();
      if (mode === "LIVE") {
        this.refreshLiveState();
      }
    }
  }

  public getAvailableBalance(): string {
    if (this.mode === "DEMO") {
      return formatSmartBalance(this.demoBalance);
    }
    return this.liveBalance ? formatSmartBalance(this.liveBalance.available_balance) : "—";
  }

  public getDemoBalance(): string {
    return formatSmartBalance(this.demoBalance);
  }

  public resetDemoBalance() {
    const rate = getLiveUsdFxRate(this.selectedCurrency);
    this.demoBalance = (10000 * rate).toFixed(2);
    this.persistDemoState();
    this.notify();
  }

  public getLiveBalance(): WalletBalance | null {
    return this.liveBalance;
  }

  public getSelectedSymbol(): string {
    return this.selectedSymbol;
  }

  public setSelectedSymbol(symbol: string) {
    this.selectedSymbol = symbol;
    if (typeof localStorage !== "undefined") {
      try { localStorage.setItem("starx_trading_selected_symbol", symbol); } catch {}
    }
    this.notify();
  }

  public getSelectedCurrency(): string {
    return this.selectedCurrency;
  }

  public setSelectedCurrency(currency: string) {
    if (this.selectedCurrency !== currency) {
      const prev = this.selectedCurrency;
      this.selectedCurrency = currency;
      this.balanceGeneration++;
      this.liveBalance = null;

      // Scale demo balance appropriately
      const currentNum = parseFloat(this.demoBalance) || 10000;
      const prevRate = getLiveUsdFxRate(prev);
      const newRate = getLiveUsdFxRate(currency);
      const usdBase = prevRate > 0 ? currentNum / prevRate : currentNum;
      this.demoBalance = Math.max(100, usdBase * newRate).toFixed(2);
      this.persistDemoState();

      if (typeof localStorage !== "undefined") {
        try { localStorage.setItem("starx_trading_currency", currency); } catch {}
      }
      this.notify();
      if (this.mode === "LIVE") {
        void this.refreshLiveState();
      }
    }
  }

  public getInstruments(): AllowedInstrument[] {
    return this.instruments;
  }

  public setInstruments(instruments: AllowedInstrument[]) {
    this.instruments = instruments;
    this.notify();
  }

  public getActiveContracts(): Contract[] {
    return this.mode === "DEMO" ? this.demoActiveContracts : this.activeContracts;
  }

  public getDemoActiveContracts(): Contract[] {
    return this.demoActiveContracts;
  }

  public getContractHistory(): Contract[] {
    return this.mode === "DEMO" ? this.demoContractHistory : this.contractHistory;
  }

  public getDemoContractHistory(): Contract[] {
    return this.demoContractHistory;
  }

  public async refreshLiveState() {
    const generation = ++this.balanceGeneration;
    const currency = this.selectedCurrency;
    const [balance, active, history, instruments, demoBalRes] = await Promise.allSettled([
      this.restClient.getBalance(currency),
      this.restClient.getActiveContracts(),
      this.restClient.getContractHistory(),
      this.restClient.getInstruments(),
      this.restClient.getDemoBalance(currency),
    ]);

    if (generation !== this.balanceGeneration || currency !== this.selectedCurrency) return;
    this.liveBalance = balance.status === "fulfilled" && balance.value.currency === currency
      ? balance.value : null;
    if (demoBalRes.status === "fulfilled" && demoBalRes.value?.available_balance) {
      const serverDemoBal = parseFloat(demoBalRes.value.available_balance);
      if (!isNaN(serverDemoBal) && serverDemoBal > 0) {
        this.demoBalance = serverDemoBal.toFixed(2);
        try { localStorage.setItem("starx_demo_balance", this.demoBalance); } catch {}
      }
    }
    if (active.status === "fulfilled")
      this.activeContracts = active.value || [];
    if (history.status === "fulfilled")
      this.contractHistory = history.value || [];
    if (instruments.status === "fulfilled" && instruments.value.length > 0) {
      this.instruments = instruments.value;
      if (
        !this.instruments.some((item) => item.symbol === this.selectedSymbol)
      ) {
        this.selectedSymbol = this.instruments[0].symbol;
      }
    }

    const failures = [balance, active, history, instruments].filter(
      (result) => result.status === "rejected",
    );
    if (failures.length > 0) {
      console.warn(
        `Failed to refresh ${failures.length} live-state resource(s)`,
      );
    }
    this.notify();
  }

  public deductDemoStake(stake: number) {
    const current = parseFloat(this.demoBalance) || 0;
    this.demoBalance = Math.max(0, current - stake).toFixed(2);
    this.persistDemoState();
    this.notify();
  }

  public creditDemoPayout(payout: number) {
    const current = parseFloat(this.demoBalance) || 0;
    this.demoBalance = (current + payout).toFixed(2);
    this.persistDemoState();
    this.notify();
  }

  public addDemoActiveContract(contract: Contract) {
    const index = this.demoActiveContracts.findIndex(
      (c) => c.contract_id === contract.contract_id,
    );
    if (index >= 0) this.demoActiveContracts[index] = contract;
    else this.demoActiveContracts.push(contract);
    this.persistDemoState();
    this.notify();
  }

  public removeDemoActiveContract(contractId: string) {
    this.demoActiveContracts = this.demoActiveContracts.filter(
      (c) => c.contract_id !== contractId,
    );
    this.persistDemoState();
    this.notify();
  }

  public addDemoContractHistory(contract: Contract) {
    this.demoContractHistory = [
      contract,
      ...this.demoContractHistory.filter(
        (c) => c.contract_id !== contract.contract_id,
      ),
    ].slice(0, 200);
    this.persistDemoState();
    this.notify();
  }
}
